import { useState, useEffect, useCallback, useRef } from 'react';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import './FindingsViewer.css';

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 72;
const TERMINAL_STATUSES = new Set(['complete', 'provisions_not_ready', 'failed']);

function shortDocName(filename) {
  return (filename || '').replace(/^\d+_/, '').replace(/\.[^.]+$/, '').replace(/_/g, ' ');
}

function HighlightedText({ text, highlight }) {
  if (!highlight || !text) return <>{text ?? ''}</>;
  const idx = text.toLowerCase().indexOf(highlight.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="fv-highlight">{text.slice(idx, idx + highlight.length)}</mark>
      {text.slice(idx + highlight.length)}
    </>
  );
}

function CollapsibleSection({ label, text, highlight }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="fv-section-row">
      <button
        className={`fv-section-toggle${open ? ' open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        {label}
      </button>
      <div className={`fv-section-text${open ? ' visible' : ''}`}>
        <HighlightedText text={text ?? ''} highlight={highlight} />
      </div>
    </div>
  );
}

function FindingCard({ finding, index, primaryDocName, refDocName, isSelf }) {
  // Self-scan filename is identical on both sides, so section alone is the distinguishing label.
  const labelA = isSelf
    ? (finding.section_a || 'Section A')
    : [shortDocName(finding.policy_a_filename || primaryDocName), finding.section_a].filter(Boolean).join(' · ');
  const labelB = isSelf
    ? (finding.section_b || 'Section B')
    : [shortDocName(finding.policy_b_filename || refDocName), finding.section_b].filter(Boolean).join(' · ');
  return (
    <div className="fv-card">
      <div className="fv-card-header">
        <span className="fv-card-number">{index + 1}.</span>
        <span className="fv-card-type">{finding.type}</span>
      </div>
      {finding.description && (
        <div className="fv-why">{finding.description}</div>
      )}
      <CollapsibleSection label={labelA || 'Unknown section'} text={finding.text_a} highlight={finding.quote_a} />
      <CollapsibleSection label={labelB || 'Unknown section'} text={finding.text_b} highlight={finding.quote_b} />
      <div className="fv-card-actions">
        <button className="fv-accept-btn" disabled title="Coming soon">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" fill="#1B6EB1"/>
            <path d="M5 8l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Accept
        </button>
        <button className="fv-reject-btn" disabled title="Coming soon">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" stroke="#dc2626" strokeWidth="1.5"/>
            <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Reject
        </button>
      </div>
    </div>
  );
}

// PC-276: analysis_status is a distinct concept from the run's lifecycle status
// (see reportHelpers.js isInProgressStatus / RUNNING|PROCESSING|... vocabulary).
// It's only present on a completed run's findings response when some Step
// Functions batches failed, and signals that coverage is partial.
function AnalysisWarningBanner({ analysisStatus }) {
  if (!analysisStatus || analysisStatus.status !== 'incomplete') return null;
  const { failed_batch_count: failedCount, total_batch_count: totalCount, coverage_estimate_percent: coverage } = analysisStatus;
  let message = `Warning: Analysis incomplete — ${failedCount} of ${totalCount} batches failed.`;
  if (typeof coverage === 'number') {
    message += ` Results cover approximately ${coverage.toFixed(1)}% of your policy library.`;
  }
  return (
    <div className="fv-analysis-warning" role="alert">
      {message}
    </div>
  );
}

function SectionBadge({ variant, docName, affected, total }) {
  const pct = total > 0 ? Math.round((affected / total) * 100) : null;
  const label = pct !== null
    ? `${shortDocName(docName)}: ${pct}% (${affected} of ${total})`
    : `${shortDocName(docName)}: ${affected} issues`;
  return (
    <span className={`fv-score-badge fv-score-badge--${variant}`}>
      <span className="fv-badge-dot" />
      {label}
    </span>
  );
}

function RunSection({ runId, primaryDocName, refDocName, filterDoc, onComplete, onFindingsLoaded, isSelf }) {
  const [state, setState] = useState({ status: 'pending', findings: [], error: null, analysisStatus: null });
  const cancelledRef = useRef(false);
  const { fetchFindings } = useApi();

  const poll = useCallback(async () => {
    try {
      const data = await fetchFindings(runId);
      if (cancelledRef.current) return 'cancelled';
      setState({ status: data.status, findings: data.findings || [], error: data.error || null, analysisStatus: data.analysis_status || null });
      if (data.status === 'complete') {
        if (onComplete) onComplete();
        if (onFindingsLoaded) onFindingsLoaded(runId, data.findings || [], data.section_counts || null);
      }
      return data.status;
    } catch (err) {
      if (cancelledRef.current) return 'cancelled';
      setState({ status: 'failed', findings: [], error: err.message });
      return 'failed';
    }
  }, [fetchFindings, runId, onComplete, onFindingsLoaded]);

  useEffect(() => {
    cancelledRef.current = false;
    let timer = null;

    const run = async (attempts = 0) => {
      if (attempts >= MAX_POLL_ATTEMPTS) {
        setState({ status: 'failed', findings: [], error: 'Timed out waiting for results.' });
        return;
      }
      const status = await poll();
      if (!cancelledRef.current && !TERMINAL_STATUSES.has(status) && status !== 'cancelled') {
        timer = setTimeout(() => run(attempts + 1), POLL_INTERVAL_MS);
      }
    };

    run();
    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [poll]);

  if (state.status === 'pending') {
    return <div className="fv-loading" role="status">Loading comparison results...</div>;
  }

  if (state.status === 'provisions_not_ready') {
    return (
      <div className="fv-error">
        Provisions not ready for <strong>{refDocName}</strong> — check back shortly or re-process the document.
      </div>
    );
  }

  if (state.status === 'failed') {
    return <div className="fv-error">Comparison failed: {state.error}</div>;
  }

  const visible = filterDoc
    ? state.findings.filter(f => f.policy_a_filename === filterDoc || f.policy_b_filename === filterDoc)
    : state.findings;

  if (visible.length === 0) {
    return (
      <>
        <AnalysisWarningBanner analysisStatus={state.analysisStatus} />
        <div className="fv-empty">No findings — no conflicts or redundancies detected.</div>
      </>
    );
  }

  return (
    <div>
      <AnalysisWarningBanner analysisStatus={state.analysisStatus} />
      {visible.map((f, i) => (
        <FindingCard key={i} finding={f} index={i} primaryDocName={primaryDocName} refDocName={refDocName} isSelf={isSelf} />
      ))}
    </div>
  );
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function FindingsViewer({ runIds, primaryDocName, referenceDocNames, onBack, onComplete, comparisonType }) {
  const { openHtmlReport } = useApi();
  const [infoOpen, setInfoOpen] = useState(true);
  const [allComplete, setAllComplete] = useState(false);

  const multipleRuns = runIds.length > 1;
  const saveReportDisabled = multipleRuns || !allComplete;
  const saveReportTitle = multipleRuns
    ? 'Report download not available for multi-reference comparisons'
    : (!allComplete ? 'Report not yet available' : undefined);

  const handleSaveReport = () => {
    if (saveReportDisabled) return;
    Promise.resolve(openHtmlReport(runIds[0])).catch((err) => console.error('Failed to open HTML report:', err));
  };
  const completedRef = useRef(0);
  useEffect(() => { completedRef.current = 0; setAllComplete(false); }, [runIds]);

  const [totalFindings, setTotalFindings] = useState(null);
  const [sectionCounts, setSectionCounts] = useState(null);
  const [uniqueDocs, setUniqueDocs] = useState([]);
  const [filterDoc, setFilterDoc] = useState(null);
  const findingCountsRef = useRef({});
  useEffect(() => {
    findingCountsRef.current = {};
    setTotalFindings(null);
    setSectionCounts(null);
    setUniqueDocs([]);
    setFilterDoc(null);
  }, [runIds]);

  const handleFindingsLoaded = useCallback((runId, findings, counts) => {
    findingCountsRef.current[runId] = findings.length;
    setTotalFindings(Object.values(findingCountsRef.current).reduce((s, n) => s + n, 0));
    if (counts) setSectionCounts(counts);
    const names = new Set();
    findings.forEach(f => {
      if (f.policy_a_filename) names.add(f.policy_a_filename);
      if (f.policy_b_filename) names.add(f.policy_b_filename);
    });
    if (names.size) setUniqueDocs(prev => [...new Set([...prev, ...names])]);
  }, []);

  const handleRunComplete = useCallback(() => {
    completedRef.current += 1;
    if (completedRef.current === runIds.length) {
      setAllComplete(true);
      if (onComplete) onComplete();
    }
  }, [runIds.length, onComplete]);

  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handler = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const isLibraryAudit = comparisonType === 'library_audit' || UUID_RE.test(primaryDocName || '');
  const isSelf = comparisonType === 'self';
  const checksTotal = totalFindings ?? 0;

  return (
    <>
      <div className="fv-header">
        <div className="fv-header-top">
          <h2>
            {isLibraryAudit
              ? <strong>Library Comparison</strong>
              : isSelf
                ? (<>Internal conflicts in <strong>{primaryDocName}</strong></>)
                : (<>Comparison on <strong>{primaryDocName}</strong>{referenceDocNames[0] && <> and <strong>{referenceDocNames[0]}</strong></>}</>)
            }
          </h2>
          <div className="fv-actions">
            <div className="fv-actions-buttons">
              <button className="fv-action-btn fv-action-btn--link" disabled title="Coming soon">Save draft</button>
              <button className="fv-action-btn fv-action-btn--outlined" onClick={handleSaveReport} disabled={saveReportDisabled} title={saveReportTitle}>Save report</button>
              <button className="fv-action-btn fv-action-btn--filled" disabled title="Coming soon">Generate new doc</button>
              <button className="fv-action-btn fv-action-btn--filled fv-action-btn--active" onClick={() => window.print()}>Export PDF</button>
            </div>
            <div className="fv-checks-complete">
              Checks complete: <span className="fv-checks-count">0/{checksTotal}</span>
            </div>
          </div>
        </div>
        {!isLibraryAudit && sectionCounts && referenceDocNames.length === 1 && (
          <div className="fv-badges-row">
            {(() => {
              const pctA = sectionCounts.a.total > 0 ? sectionCounts.a.affected / sectionCounts.a.total : 0;
              const pctB = sectionCounts.b.total > 0 ? sectionCounts.b.affected / sectionCounts.b.total : 0;
              const aIsWorse = pctA >= pctB;
              return (
                <>
                  <SectionBadge
                    variant={aIsWorse ? 'error' : 'success'}
                    docName={primaryDocName}
                    affected={sectionCounts.a.affected}
                    total={sectionCounts.a.total}
                  />
                  <SectionBadge
                    variant={aIsWorse ? 'success' : 'error'}
                    docName={referenceDocNames[0]}
                    affected={sectionCounts.b.affected}
                    total={sectionCounts.b.total}
                  />
                </>
              );
            })()}
          </div>
        )}
      </div>
      <div className="fv-container">
        {onBack && (
          <button className="pc-new-comparison-link" onClick={onBack}>← New comparison</button>
        )}
        <div className="fv-info-accordion">
          <button className="fv-info-toggle" onClick={() => setInfoOpen((v) => !v)}>
            <span className="fv-info-toggle-left">
              <span className="fv-info-icon">ⓘ</span>
              How to review proposed changes
            </span>
            {infoOpen ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
          </button>
          {infoOpen && (
            <ul className="fv-info-body">
              <li>Accept: The proposed change will be added to the newly generated document or report.</li>
              <li>Reject: The content will remain as per the most recent file in the newly generated document or report.</li>
              <li>No action: If you do not select either option, the content will remain as per the most recent file newly generated document or report.</li>
            </ul>
          )}
        </div>

        {isLibraryAudit && uniqueDocs.length > 1 && (
          <div className="fv-filter-row">
            <label htmlFor="fv-doc-filter" className="fv-filter-label">Filter by document</label>
            <select
              id="fv-doc-filter"
              aria-label="Filter by document"
              className="fv-filter-select"
              value={filterDoc || ''}
              onChange={e => setFilterDoc(e.target.value || null)}
            >
              <option value="">All documents</option>
              {uniqueDocs.map(doc => (
                <option key={doc} value={doc}>{shortDocName(doc)}</option>
              ))}
            </select>
          </div>
        )}

        {runIds.map((runId, i) => (
          <div key={runId} className="fv-run-section">
            {multipleRuns && (
              <div className="fv-run-heading">vs. {referenceDocNames[i] || runId}</div>
            )}
            <RunSection
              runId={runId}
              primaryDocName={primaryDocName}
              refDocName={referenceDocNames[i] || runId}
              filterDoc={filterDoc}
              onComplete={handleRunComplete}
              onFindingsLoaded={handleFindingsLoaded}
              isSelf={isSelf}
            />
          </div>
        ))}

        {showScrollTop && (
          <button
            className="fv-scroll-top"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Scroll to top"
          >
            ↑
          </button>
        )}
      </div>
    </>
  );
}
